import { Query } from '@apollo/client/react/components'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import uniqWith from 'lodash/uniqWith'

import { appliedFiltersToGraphQLVariables } from 'utils/filters/utils'
import { relayConnectionToArray } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas, graphene } from 'graphql/tags'
import { GROUP_BY_STYLE_TAG_ID } from 'store/shop/constants'

import { transformWarehouses } from 'modals/Shop/ProductDetailModal/ProductDetailModal.transformations'

import {
  COLLECTIONS_KEY,
  DELIVERY_WINDOW_KEY,
  EXCLUDE_FILTER_FROM_GRAPHQL_KEYS,
  formatDeliveryWindow,
  getCatalogFilters,
} from './CatalogFilters/filters'
import { priceTypeMatchesFragment } from './ProductCatalog.fragments'
import { useVariantsInCart } from './ProductCatalog.hooks'
import { NOTE_VARIANTS } from './ProductCatalog.mutations'
import {
  addAppliedTraitFiltersToTraitsFilter,
  formatStyletagFilters,
  getInventoryBySkuId,
  getInventoryByVariantColor,
  getUniqGroupedByTags,
} from './ProductCatalog.transformations'

export const PAGE_SIZE = 20

const combinedCollectionsAndProductsQuery = atlas`#graphql
  query CombinedCollectionsAndProducts(
    $limit: Int
    $offset: Int
    $collectionProductsFilters: ProductsCollectionFilter
    $collectionsBrandIds: [ID!]
    $collectionProductsBrandIds: [ID!]
    $priceTypeId: ID!
    $groupBy: CollectionProductGroupByInput
    $orderBy: CollectionProductOrderByInput
    $productVideosAreEnabled: Boolean!
    $externalMediaEnabled: Boolean!
  ) {
    collections(
      brandIds: $collectionsBrandIds
      filters: { archived: false }
      orderBy: { field: NAME }
    ) {
      edges {
        node {
          id
          name
          description
          madeToOrder
          delivery {
            id
            startShipDate
            completeShipDate
          }
          season {
            id
            year
            name
            code
          }
        }
      }
    }
    collectionProducts(
      limit: $limit
      offset: $offset
      filter: $collectionProductsFilters
      brandIds: $collectionProductsBrandIds
      groupBy: $groupBy
      orderBy: $orderBy
    ) {
      edges {
        node {
          id
          groupedBy {
            id
            name
            value
          }
          comments
          skus {
            edges {
              node {
                id
              }
            }
          }
          casepacks {
            edges {
              node {
                id
              }
            }
          }
          product {
            id
            name
            externalIdentifier
            badges {
              edges {
                node {
                  id
                  name
                  image {
                    url
                  }
                }
              }
            }
            traitValues {
              edges {
                node {
                  id
                  trait {
                    id
                    code
                    name
                  }
                  value
                }
              }
            }
            images {
              url
            }
            videos @include(if: $productVideosAreEnabled) {
              id
              url
            }
            externalMedia @include(if: $externalMediaEnabled) {
              id
              url
              type
              format
              thumbnailUrl
              payload
            }
          }
          priceRange(priceTypeId: $priceTypeId) {
            minWholesaleValue
            maxWholesaleValue
            minRetailValue
            maxRetailValue
          }
          collection {
            id
          }
          variants {
            edges {
            values {
                edges {
                  node {
                    id
                    value
                    trait {
                      id
                      code
                      name
                    }
                    externalIdentifier
                    displayDetails {
                      merchandiseImages {
                        url
                      }
                      hexColor
                      swatchImage {
                        url
                      }
                    }
                  }
                }
              }
              node {
                id
                code
                name
              }
            }
          }
        }
      }
    }
  }
`

export const withCombinedCollectionsAndProducts =
  (WrappedComponent) => (props) => {
    const {
      searchText,
      brandId,
      selectedPriceType,
      orderWasNotStarted,
      localStorageLoaded,
      isGroupByTagInfoSet,
      isSortByTagInfoSet,
      sortByTagInfo,
      appliedFilters,
      groupByTagInfo,
      userDateFormat,
      selectedWarehouse,
      shouldHideSoldOutColors,
    } = props

    const {
      localizeUserData,
      productVideos,
      orb360ProductVideos: orbVideosAreEnabled,
      vntana: vntanaIsEnabled,
      skipExternalMedia,
    } = useFlags()

    const restFilters = omit(appliedFilters, EXCLUDE_FILTER_FROM_GRAPHQL_KEYS)
    const catalogFilters = getCatalogFilters(localizeUserData)
    const collectionProductsFilters = addAppliedTraitFiltersToTraitsFilter(
      {
        [DELIVERY_WINDOW_KEY]: formatDeliveryWindow(
          appliedFilters[DELIVERY_WINDOW_KEY],
          userDateFormat,
        ),
        ...formatStyletagFilters(
          appliedFiltersToGraphQLVariables(catalogFilters, restFilters),
        ),
        ...(selectedWarehouse?.id && shouldHideSoldOutColors
          ? {
              inventoryFilter: {
                warehouseIds: [selectedWarehouse.id],
                availability: 'ALL',
                unitsRange: {
                  min: 1,
                },
              },
            }
          : {}),
        searchText,
      },
      appliedFilters,
    )

    const groupByTagInfoId = get(groupByTagInfo, 'id')
    const queryVariables = {
      collectionsBrandIds: [brandId],
      collectionProductsBrandIds: [brandId],
      limit: PAGE_SIZE,
      collectionProductsFilters,
      ...(isGroupByTagInfoSet && {
        groupBy: {
          ...(get(groupByTagInfo, 'code') && {
            groupBy: groupByTagInfo.code,
          }),
          ...(groupByTagInfoId &&
            get(groupByTagInfo, 'code') === GROUP_BY_STYLE_TAG_ID && {
              subGroupById: groupByTagInfo.id,
            }),
        },
      }),
      ...(isSortByTagInfoSet && {
        orderBy: {
          ...(sortByTagInfo?.code === 'PRICE' && {
            price: {
              priceTypeId: selectedPriceType.id,
              order: sortByTagInfo.value,
            },
          }),
        },
      }),
      priceTypeId: selectedPriceType.id,
      productVideosAreEnabled: !!productVideos,
      externalMediaEnabled:
        (orbVideosAreEnabled || vntanaIsEnabled) && !skipExternalMedia,
    }

    return (
      <Query
        client={atlasClient}
        query={combinedCollectionsAndProductsQuery}
        variables={{
          offset: 0,
          ...queryVariables,
        }}
        skip={
          orderWasNotStarted ||
          !localStorageLoaded ||
          !brandId ||
          !get(selectedPriceType, 'id')
        }
        notifyOnNetworkStatusChange
      >
        {({ loading, error, data = {}, fetchMore }) => {
          const collections =
            (!loading || data.collections) && !error && data
              ? relayConnectionToArray(data.collections)
              : []

          const collectionProducts =
            (!loading || data.collectionProducts) && !error && data
              ? relayConnectionToArray(data.collectionProducts)
              : []

          let productsWithoutPrice = []
          if (!loading) {
            productsWithoutPrice = collectionProducts
              .filter((p) => p.priceRange?.minRetailValue === null)
              .map((p) => p.product?.id)
              .sort()
          }

          const uniqCollections = uniqBy(
            collectionProducts,
            (collectionProduct) => get(collectionProduct, 'collection.id'),
          )
          const productIds = collectionProducts.map((collectionProduct) =>
            get(collectionProduct, 'product.id', ''),
          )

          const seasons = collections.reduce((acc, collection) => {
            const season = get(collection, 'season')
            return [...acc, ...(season ? [season] : [])]
          }, [])

          const customProps = {
            ...props,
            collectionsLoading: loading,
            collections,
            seasons: uniqBy(seasons, 'id'),
            productsWithoutPrice,
            productsGroupByCollectionLoading: loading,
            collectionProducts,
            collectionIdsShown: uniqCollections.map(
              ({ collection }) => collection.id,
            ),
            productIds: uniq(productIds),
            totalProductsShown:
              data && (!loading || data.collectionProducts) && !error
                ? uniqWith(
                    relayConnectionToArray(data.collectionProducts),
                    (collectionProduct, element) =>
                      get(collectionProduct, 'product.id') ===
                        get(element, 'product.id') &&
                      get(collectionProduct, 'collection.id') ===
                        get(element, 'collection.id'),
                  ).length
                : 0,
            groupByTagsShown: !isGroupByTagInfoSet
              ? {}
              : getUniqGroupedByTags(collectionProducts, groupByTagInfo),
            loadMoreItems: () =>
              fetchMore({
                client: atlasClient,
                variables: {
                  offset: get(data, 'collectionProducts.edges', []).length,
                  ...queryVariables,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  const newEdges = get(
                    fetchMoreResult,
                    'collectionProducts.edges',
                  )
                  return newEdges.length
                    ? {
                        collectionProducts: {
                          ...get(previousResult, 'collectionProducts'),
                          edges: [
                            ...get(
                              previousResult,
                              'collectionProducts.edges',
                              [],
                            ),
                            ...newEdges,
                          ],
                        },
                        // Collections data remains the same for subsequent fetchMore calls
                        collections: get(previousResult, 'collections'),
                      }
                    : previousResult
                },
              }),
          }

          return <WrappedComponent {...customProps} />
        }}
      </Query>
    )
  }

const collectionProductsGroupTotalQuery = atlas`#graphql
  query collectionProductsGroupTotalQuery(
    $filter: ProductsCollectionFilter
    $brandIds: [ID!]
  ) {
    collectionProductsGroupTotal(filter: $filter, brandIds: $brandIds) {
      total
      collection {
        id
      }
    }
  }
`

export const withCollectionProductGroupTotal =
  (WrappedComponent) => (props) => {
    const {
      searchText,
      collectionIdsShown,
      appliedFilters,
      brandId,
      localStorageLoaded,
      productsGroupByCollectionLoading,
      userDateFormat,
      shouldHideSoldOutColors,
      selectedWarehouse,
    } = props
    const { localizeUserData } = useFlags()
    const catalogFilters = getCatalogFilters(localizeUserData)
    const restFilters = omit(appliedFilters, EXCLUDE_FILTER_FROM_GRAPHQL_KEYS)
    const filter = addAppliedTraitFiltersToTraitsFilter(
      {
        [DELIVERY_WINDOW_KEY]: formatDeliveryWindow(
          appliedFilters[DELIVERY_WINDOW_KEY],
          userDateFormat,
        ),
        ...formatStyletagFilters(
          appliedFiltersToGraphQLVariables(catalogFilters, restFilters),
        ),
        [COLLECTIONS_KEY]: collectionIdsShown,
        ...(selectedWarehouse?.id && shouldHideSoldOutColors
          ? {
              inventoryFilter: {
                warehouseIds: [selectedWarehouse.id],
                availability: 'ALL',
                unitsRange: {
                  min: 1,
                },
              },
            }
          : {}),
        searchText,
      },
      appliedFilters,
    )

    return (
      <Query
        client={atlasClient}
        query={collectionProductsGroupTotalQuery}
        variables={{
          filter,
          brandIds: [brandId],
        }}
        skip={
          isEmpty(collectionIdsShown) ||
          !localStorageLoaded ||
          productsGroupByCollectionLoading ||
          !brandId
        }
        refetch={selectedWarehouse?.id && shouldHideSoldOutColors}
        notifyOnNetworkStatusChange
      >
        {({ loading, error, data }) => {
          const totalProductsPerCollection =
            !loading && !error && data ? data.collectionProductsGroupTotal : []
          const customProps = {
            ...props,
            collectionProductGroupTotalLoading: loading,
            totalProductsPerCollection,
          }
          return <WrappedComponent {...customProps} />
        }}
      </Query>
    )
  }

const collectionProductsTotalQuery = atlas`#graphql
  query collectionProductsTotalQueryDupe1(
    $filter: ProductsCollectionFilter
    $brandIds: [ID!]
  ) {
    collectionProductsTotal(filter: $filter, brandIds: $brandIds)
  }
`

export const withCollectionProductsTotal = (WrappedComponent) => (props) => {
  const {
    searchText,
    appliedFilters,
    brandId,
    userDateFormat,
    shouldHideSoldOutColors,
    selectedWarehouse,
  } = props
  const restFilters = omit(appliedFilters, EXCLUDE_FILTER_FROM_GRAPHQL_KEYS)
  const { localizeUserData } = useFlags()
  const catalogFilters = getCatalogFilters(localizeUserData)
  const filter = addAppliedTraitFiltersToTraitsFilter(
    {
      [DELIVERY_WINDOW_KEY]: formatDeliveryWindow(
        appliedFilters[DELIVERY_WINDOW_KEY],
        userDateFormat,
      ),
      ...formatStyletagFilters(
        appliedFiltersToGraphQLVariables(catalogFilters, restFilters),
      ),
      ...(selectedWarehouse?.id && shouldHideSoldOutColors
        ? {
            inventoryFilter: {
              warehouseIds: [selectedWarehouse.id],
              availability: 'ALL',
              unitsRange: {
                min: 1,
              },
            },
          }
        : {}),
      searchText,
    },
    appliedFilters,
  )

  return (
    <Query
      client={atlasClient}
      query={collectionProductsTotalQuery}
      variables={{
        filter,
        brandIds: [brandId],
      }}
      skip={!props.localStorageLoaded || !brandId || props.orderWasNotStarted}
      notifyOnNetworkStatusChange
      refetch={selectedWarehouse?.id && shouldHideSoldOutColors}
    >
      {({ loading, error, data }) => {
        const customProps = {
          ...props,
          totalProductsLoading: loading,
          totalItems:
            !loading && !error && data ? data.collectionProductsTotal : 0,
        }
        return <WrappedComponent {...customProps} />
      }}
    </Query>
  )
}

const warehousesQuery = atlas`#graphql
  query WarehousesQueryDupe1($brandId: ID) {
    warehouses(brandId: $brandId) {
      edges {
        node {
          id
          name
          code
          availabilityGroups {
            edges {
              node {
                id
                availability {
                  availableOn
                  isImmediate
                }
                isEmpty
              }
            }
          }
        }
      }
    }
  }
`

export const withWarehouses = (WrappedComponent) => (props) => {
  const { brandId, orderWasNotStarted } = props
  return (
    <Query
      client={atlasClient}
      query={warehousesQuery}
      variables={{ brandId }}
      skip={!brandId || orderWasNotStarted}
    >
      {({ loading, error, data }) => {
        const customProps = {
          ...props,
          warehouses:
            !loading && !error && data
              ? relayConnectionToArray(data.warehouses)
              : [],
          brandWarehousesWithInventory:
            !loading && !error && data
              ? transformWarehouses(data.warehouses)
              : [],
          warehousesQueryLoading: loading,
        }
        return <WrappedComponent {...customProps} />
      }}
    </Query>
  )
}

const warehousesForRetailerQuery = graphene`#graphql
  query warehousesForRetailerQuery($retailerId: ID!) {
    warehousesForRetailer(filterBy: { retailerId: $retailerId }) {
      edges {
        node {
          id
          name
          code
          default
        }
      }
    }
  }
`

const warehousesFromBrandQuery = graphene`#graphql
  query warehousesFromBrandQuery($brandId: ID!) {
    warehousesFromBrand(filterBy: { brandId: $brandId }) {
      edges {
        node {
          id
          name
          code
          default
        }
      }
    }
  }
`

export const withWarehousesFromConnection = (WrappedComponent) => (props) => {
  const {
    isAccountTypeRetailer,
    retailerId,
    brandId,
    orderWasNotStarted,
    orphanRetailer,
  } = props
  const shouldUseBrandWarehouses =
    isAccountTypeRetailer || orphanRetailer?.retailerEmail
  const query = shouldUseBrandWarehouses
    ? warehousesFromBrandQuery
    : warehousesForRetailerQuery
  const dataProperty = shouldUseBrandWarehouses
    ? 'warehousesFromBrand'
    : 'warehousesForRetailer'
  const variables = {
    ...(shouldUseBrandWarehouses && { brandId }),
    ...(!shouldUseBrandWarehouses && { retailerId }),
  }

  return (
    <Query
      query={query}
      variables={variables}
      skip={
        orderWasNotStarted || (isAccountTypeRetailer ? !brandId : !retailerId)
      }
    >
      {({ loading, error, data }) => {
        const customProps = {
          ...props,
          warehousesFromConnection:
            !loading && !error && data
              ? relayConnectionToArray(data[dataProperty])
              : [],
          warehousesFromConnectionLoading: loading,
        }
        return <WrappedComponent {...customProps} />
      }}
    </Query>
  )
}

const aggregateSkuInventoryItemsQuery = atlas`#graphql
  query aggregateSkuInventoryItemsDupe1(
    $warehouseIds: [ID!]!
    $filter: AggregateSkuInventoryItemsFilter
  ) {
    aggregateSkuInventoryItems(
      warehouseIds: $warehouseIds
      includeImmediate: true
      filter: $filter
    ) {
      items {
        availableQuantity
        skuId
      }
    }
  }
`
export const withAggregateSkuInventoryItems = (WrappedComponent) => (props) => {
  const {
    warehouseQueriesLoading,
    productsGroupByCollectionLoading,
    productIds,
    selectedWarehouse,
    warehouseIds,
  } = props
  const { id: selectedWarehouseId } = selectedWarehouse || {}
  // TODO(70330): find a more performant solution to query only the new ids
  return (
    <Query
      client={atlasClient}
      query={aggregateSkuInventoryItemsQuery}
      variables={{
        warehouseIds: selectedWarehouseId
          ? [selectedWarehouseId]
          : warehouseIds,
        filter: { productIds },
      }}
      skip={
        warehouseQueriesLoading ||
        productsGroupByCollectionLoading ||
        !productIds.length
      }
      notifyOnNetworkStatusChange
    >
      {({ loading, error, data }) => {
        const customProps = {
          ...props,
          aggregateSkuInventoryItemsQueryLoading: loading,
          inventoryByVariantColor:
            !loading && !error && data
              ? getInventoryByVariantColor(data.aggregateSkuInventoryItems)
              : new Map(),
          inventoryBySkuId:
            !loading && !error && data
              ? getInventoryBySkuId(data.aggregateSkuInventoryItems)
              : new Map(),
        }
        return <WrappedComponent {...customProps} />
      }}
    </Query>
  )
}

export const priceTypesQuery = graphene`#graphql
  query priceTypesQuery($accountId: ID!) {
    node(id: $accountId) {
      ... on RetailerAccount {
        id
        ...priceTypeMatches
      }
    }
  }
  ${priceTypeMatchesFragment}
`

export const VARIANTS_IN_CART = atlas`#graphql
  query variantsInCart($brandId: ID!, $retailerIds: [ID!], $orphanRetailerIds: [ID!]) {
    variantsInCart(
      filterBy: { brandIds: [$brandId], retailerIds: $retailerIds, orphanRetailerIds: $orphanRetailerIds }
    ) {
      variantId
      orderId
      collection {
        id
        name
      }
      product {
        id
      }
      priceType {
        id
        name
        currency {
          id
          name
          code
        }
      }
      warehouse {
        id
        name
      }
      door {
        id
        name
        code
      }
    }
  }
`

// Data necessary for styles noting.
export const withNoting = (Wrapped) => (props) => {
  const { brandId, eventId, retailerId, typeId, orphanRetailer } = props

  const getOrderInfo = (orphanRetailerId) => {
    const orderInfo = {
      // For orphan retailers, we use the brandId as the retailerId
      retailerId: orphanRetailerId ? brandId : retailerId,
      brandId,
      priceTypeId: props?.selectedPriceType?.id,
      warehouseId: props?.selectedWarehouse?.id,
      typeId,
      eventId,
      orphanRetailerId,
    }
    return orderInfo
  }

  const addVariantsToCart = async ({
    collectionId,
    variantIds,
    orphanRetailerId,
  }) => {
    const orderInfo = {
      ...getOrderInfo(orphanRetailerId),
      collectionId,
    }

    const res = await atlasClient.mutate({
      mutation: NOTE_VARIANTS,
      variables: {
        input: {
          orderInfo,
          variantItems: variantIds.reduce((acc, variantId) => {
            const newElements = props.selectedDoors.length
              ? props.selectedDoors.map(({ value }) => ({
                  variantId,
                  doorId: value,
                }))
              : [{ variantId }]

            return [...acc, ...newElements]
          }, []),
        },
      },
      refetchQueries: [
        {
          query: VARIANTS_IN_CART,
          variables: {
            brandId,
            retailerIds: orphanRetailerId ? undefined : [retailerId],
            orphanRetailerIds: retailerId ? undefined : [orphanRetailerId],
          },
        },
      ],
    })
    return res
  }

  const { variantsInCart, refetchVariantsInCart } = useVariantsInCart(
    brandId,
    retailerId,
    orphanRetailer?.id,
  )

  return (
    <Wrapped
      {...props}
      variantsInCart={variantsInCart}
      addVariantsToCart={addVariantsToCart}
      getOrderInfo={getOrderInfo}
      refetchVariantsInCart={refetchVariantsInCart}
    />
  )
}
